import React, { useState, useEffect } from 'react';
import { quizService } from '../services/quizService';
import { useAuth } from './Auth/AuthProvider';
import QuizStats from './QuizStats';
import AdBanner from './AdBanner/AdBanner';
import './QuizSettings.css';

const QuizSettings = ({ quiz, totalQuestions, onStartQuiz, onCancel, isPublicAccess = false, isSharedQuiz = false, originalUserId = null }) => {
  const { user } = useAuth();
  const [questionCount, setQuestionCount] = useState(isPublicAccess ? 10 : (isSharedQuiz && user ? 10 : 5));
  const [orderMode, setOrderMode] = useState(isPublicAccess ? 'random' : 'order');
  const [continueMode, setContinueMode] = useState(true);
  const [startFromQuestion, setStartFromQuestion] = useState(1);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [incorrectQuestions, setIncorrectQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(!!quiz?.isFavorite);
  const [favBusy, setFavBusy] = useState(false);

  const availableQuestionCounts = [5, 10, 15, 20];

  useEffect(() => {
    if (user && quiz) {
      const fetchQuizHistory = async () => {
        try {
          setLoading(true);
          let answered, incorrect;
          
          console.log('QuizSettings: Fetching quiz history', { 
            userId: user.uid, 
            quizId: quiz.id, 
            isSharedQuiz, 
            isPublicAccess 
          });
          
          if (isSharedQuiz) {
            // Shared quiz: fetch from user's shared_quizzes collection
            [answered, incorrect] = await Promise.all([
              quizService.getSharedQuizAnsweredQuestions(user.uid, quiz.id),
              quizService.getSharedQuizIncorrectQuestions(user.uid, quiz.id)
            ]);
            console.log('QuizSettings: Shared quiz stats loaded', { 
              answeredCount: answered.length, 
              incorrectCount: incorrect.length,
              answered,
              incorrect
            });
          } else {
            // Own quiz: fetch from user's own quizzes collection
            [answered, incorrect] = await Promise.all([
              quizService.getAnsweredQuestions(user.uid, quiz.id),
              quizService.getIncorrectQuestions(user.uid, quiz.id)
            ]);
            console.log('QuizSettings: Own quiz stats loaded', { 
              answeredCount: answered.length, 
              incorrectCount: incorrect.length 
            });
          }
          
          setAnsweredQuestions(answered);
          setIncorrectQuestions(incorrect);
        } catch (error) {
          console.error('問題履歴の取得に失敗しました:', error);
          // If fetching shared quiz history fails, set empty arrays to avoid breaking the UI
          setAnsweredQuestions([]);
          setIncorrectQuestions([]);
        } finally {
          setLoading(false);
        }
      };
      
      fetchQuizHistory();
    }
  }, [user, quiz, isSharedQuiz]);

  // Initialize favorite state (shared vs own)
  useEffect(() => {
    const initFavorite = async () => {
      try {
        if (!user || !quiz) return;
        if (isSharedQuiz && originalUserId) {
          const fav = await quizService.isQuizFavorited(user.uid, originalUserId, quiz.id);
          setIsFavorite(fav);
        } else {
          setIsFavorite(!!quiz.isFavorite);
        }
      } catch (e) {
        console.warn('お気に入り状態の初期化に失敗', e);
      }
    };
    initFavorite();
  }, [user, quiz, isSharedQuiz, originalUserId]);

  const handleToggleFavorite = async () => {
    if (!user) return;
    if (!quiz) return;
    if (favBusy) return;
    setFavBusy(true);
    try {
      if (isSharedQuiz && originalUserId) {
        if (isFavorite) {
          await quizService.removeFromFavorites(user.uid, originalUserId, quiz.id);
          setIsFavorite(false);
        } else {
          await quizService.addToFavorites(user.uid, originalUserId, quiz.id);
          setIsFavorite(true);
        }
      } else {
        const newState = await quizService.toggleOwnQuizFavorite(user.uid, quiz.id);
        setIsFavorite(newState);
      }
    } catch (err) {
      console.error('お気に入り切替に失敗しました', err);
      alert('お気に入りの更新に失敗しました');
    } finally {
      setFavBusy(false);
    }
  };

  const handleStartQuiz = () => {
    const settings = {
      questionCount: Math.min(questionCount, totalQuestions),
      orderMode,
      continueMode,
      startFromQuestion: continueMode ? null : startFromQuestion,
      answeredQuestions,
      incorrectQuestions
    };
    onStartQuiz(settings);
  };

  return (
    <div className="quiz-settings">
      <div className="quiz-settings-container">
        <div className="quiz-settings-left"></div>
        <div className="quiz-settings-main">
          <div className="quiz-settings-header">
            <h2>クイズ設定</h2>
            <button onClick={onCancel} className="close-btn">
              ← 戻る
            </button>
          </div>

      <div className="quiz-info">
        <h3>{quiz.title}</h3>
        <p>{quiz.description}</p>
        <div className="quiz-meta">
          <span>総問題数: {totalQuestions}問</span>
        </div>
      </div>

      {user && (
        <div className="favorite-actions">
          <button
            className={`favorite-add-btn ${isFavorite ? 'favorited' : ''}`}
            onClick={handleToggleFavorite}
            disabled={favBusy}
            title={'お気に入りのオン/オフ'}
          >
            <span className="star">★</span>
            <span className="label">お気に入りに追加</span>
          </button>
        </div>
      )}

      {user && (
        <QuizStats 
          totalQuestions={totalQuestions}
          answeredQuestions={answeredQuestions}
          incorrectQuestions={incorrectQuestions}
          loading={loading}
        />
      )}

      <div className="settings-form">
        {isPublicAccess && (
          <div className="public-access-notice">
            <div className="notice-content">
              <h4>📢 公開クイズ制限</h4>
              <p>公開クイズは、<strong>10問ランダム出題</strong>のみご利用いただけます。</p>
              {!user && (
                <>
                  <p>より多くの機能を利用するには、アカウントを作成してログインしてください。</p>
                  <div className="login-buttons">
                    <button onClick={() => window.location.href = '/login'} className="login-btn">
                      ログイン
                    </button>
                    <button onClick={() => window.location.href = '/login?tab=signup'} className="signup-btn">
                      アカウント作成
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {user && isSharedQuiz && !isPublicAccess && (
          <div className="logged-in-shared-notice">
            <div className="notice-content">
              <h4>✅ ログイン済み</h4>
              <p>ログインユーザーは<strong>すべての設定オプション</strong>をご利用いただけます。</p>
            </div>
          </div>
        )}

        <div className="setting-group">
          <label>出題問題数</label>
          <div className="question-count-selector">
            {availableQuestionCounts.map(count => (
              <label key={count} className="radio-option">
                <input
                  type="radio"
                  value={count}
                  checked={questionCount === count}
                  onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                  disabled={count > totalQuestions || (isPublicAccess && count !== 10)}
                />
                <span className={count > totalQuestions || (isPublicAccess && count !== 10) ? 'disabled' : ''}>
                  {count}問
                  {!isPublicAccess && count > totalQuestions && ' (利用不可)'}
                  {isPublicAccess && count !== 10 && ' (公開クイズでは利用不可)'}
                </span>
              </label>
            ))}
          </div>
          <p className="setting-note">
            {isPublicAccess ? 
              '公開クイズは10問のみ選択可能です。' :
              '選択した問題数が総問題数を超える場合、実際の問題数に調整されます。'
            }
          </p>
        </div>

        <div className="setting-group">
          <label>出題順序</label>
          <div className="order-selector">
            <label className="radio-option">
              <input
                type="radio"
                value="order"
                checked={orderMode === 'order'}
                onChange={(e) => setOrderMode(e.target.value)}
                disabled={isPublicAccess}
              />
              <span className={isPublicAccess ? 'disabled' : ''}>
                番号順
                {isPublicAccess && ' (公開クイズでは利用不可)'}
              </span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                value="random"
                checked={orderMode === 'random'}
                onChange={(e) => setOrderMode(e.target.value)}
                disabled={false}
              />
              <span>ランダム</span>
            </label>
          </div>
          <p className="setting-note">
            {isPublicAccess ? 
              '公開クイズはランダム出題のみ利用可能です。' :
              '番号順: 問題番号の順序で出題\nランダム: 問題をランダムに出題'
            }
          </p>
        </div>

        {orderMode === 'order' && !isPublicAccess && (
          <div className="setting-group">
            <label>番号順出題設定</label>
            <div className="order-options">
              <label className="radio-option">
                <input
                  type="radio"
                  value="continue"
                  checked={continueMode === true}
                  onChange={() => setContinueMode(true)}
                />
                <span>続きから ({answeredQuestions.length}問済み)</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  value="incorrect-only"
                  checked={continueMode === 'incorrect'}
                  onChange={() => setContinueMode('incorrect')}
                />
                <span>不正解のみ ({incorrectQuestions.length}問)</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  value="from-start"
                  checked={continueMode === false}
                  onChange={() => setContinueMode(false)}
                />
                <span>最初から</span>
              </label>
            </div>
            
            {continueMode === false && (
              <div className="start-question-selector">
                <label htmlFor="start-question">開始問題番号:</label>
                <input
                  id="start-question"
                  type="number"
                  min="1"
                  max={totalQuestions}
                  value={startFromQuestion}
                  onChange={(e) => setStartFromQuestion(parseInt(e.target.value))}
                />
              </div>
            )}
            
            <p className="setting-note">
              続きから: 未出題の問題のみを出題<br/>
              不正解のみ: 不正解だった問題のみを出題<br/>
              最初から: 指定した問題番号から順番に出題
            </p>
          </div>
        )}

        <div className="settings-summary">
          <h4>設定サマリー</h4>
          <ul>
            <li>出題問題数: {Math.min(questionCount, totalQuestions)}問 / {totalQuestions}問</li>
            <li>出題順序: {orderMode === 'random' ? 'ランダム' : '番号順'}</li>
            {orderMode === 'order' && !isPublicAccess && (
              <li>
                番号順設定: {
                  continueMode === true ? `続きから (${answeredQuestions.length}問済み)` :
                  continueMode === 'incorrect' ? `不正解のみ (${incorrectQuestions.length}問)` :
                  `${startFromQuestion}問目から`
                }
              </li>
            )}
            {isPublicAccess && (
              <li>アクセス制限: 公開クイズ（10問ランダムのみ）</li>
            )}
          </ul>
        </div>

        <div className="settings-actions">
          <button onClick={onCancel} className="cancel-btn">
            キャンセル
          </button>
          <button onClick={handleStartQuiz} className="start-btn">
            クイズを開始
          </button>
        </div>
      </div>
      
        </div>
        
        <div className="quiz-settings-sidebar">
          <AdBanner />
        </div>
      </div>
    </div>
  );
};

export default QuizSettings;
